import {useMemo, useState, useEffect} from "react";
import axios from "services/axios";
import dayjs from "dayjs";
import {useSelector} from "react-redux";
import {DashboardLayout} from "components/Layout";
import {BasicTable, SearchBox} from "components/Table";
import {DatePicker} from "components/DatePicker";
import {BasicDropdown} from "components/Dropdown";
import {useStaffAttendanceAdminHospital} from "hooks/AdminHospital/Attendance";
import {useHistory} from "react-router-dom";

// data fetching moved to react-query hook

// legacy CSV export removed; using server-side export endpoint instead

export function StaffAttendanceAdminHospitalPage() {
	const selectedHospital = useSelector(state => state.HospitalsReducer.selectedHospital);
	const hospitalId = selectedHospital?.Hospital?.id;
	const hospitalName = selectedHospital?.Hospital?.name;

	const [dateRange, setDateRange] = useState({
		startDate: dayjs().format("YYYY-MM-DD"),
		endDate: dayjs().format("YYYY-MM-DD"),
	});
	const [dataLimit, setDataLimit] = useState(10);
	const [dateDisplay, setDateDisplay] = useState("");
	const {data, setFilter, isLoading, filter} = useStaffAttendanceAdminHospital(hospitalId);
	const [exporting, setExporting] = useState(false);
	const history = useHistory();

	useEffect(() => {
		setFilter(prev => ({
			...prev,
			hospitalId: hospitalId || null,
			date: undefined,
			startDate: dateRange.startDate,
			endDate: dateRange.endDate,
			page: 1,
		}));
	}, [hospitalId, dateRange, setFilter]);

	const columns = useMemo(
		() => [
			{Header: "No", accessor: "idSerial", width: 60, centered: true},
			{Header: "Nama Staff", accessor: "staffName"},
			{Header: "Durasi Jam Kerja", accessor: "durationHM", centered: true},
			{
				Header: "Aksi",
				accessor: "action",
				centered: true,
				Cell: ({row}) => {
					const userId = row?.original?.userId;

					return (
						<a
							class="text-orange"
							style={{cursor: "pointer"}}
							onClick={() =>
								history.push({
									pathname: `/hospital/absensi-staff/detail/${userId}`,
									state: {
										startDate: filter?.startDate || dateRange.startDate,
										endDate: filter?.endDate || dateRange.endDate,
										search: filter?.search || "",
									},
								})
							}>
							View Detail
						</a>
					);
				},
			},
		],
		[history, filter?.startDate, filter?.endDate, filter?.search, dateRange.startDate, dateRange.endDate],
	);

	const formatDurationHM = secs => {
		const totalMinutes = Math.floor((secs || 0) / 60);
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;

		return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
	};

	const rows = useMemo(() => data?.data?.rows || [], [data?.data?.rows]);

	const displayRows = useMemo(() => {
		return rows.map((r, idx) => ({
			...r,
			idSerial: r.idSerial ?? idx + 1,
			durationHM: formatDurationHM(r?.userDurationSeconds),
		}));
	}, [rows]);

	const handleExport = async () => {
		try {
			setExporting(true);
			const params = {
				hospitalId,
				search: filter?.search || undefined,
				checkInDate: `${filter?.startDate || dateRange.startDate},${filter?.endDate || dateRange.endDate}`,
				orderBy: filter?.orderBy || "checkInDate",
				sortBy: filter?.sortBy || "DESC",
			};

			const res = await axios.get("/staff/attendances/export", {
				params,
				responseType: "blob",
			});

			const blob = res?.data;
			const disposition = res?.headers?.["content-disposition"] || res?.headers?.["Content-Disposition"];
			let filename = `absensi-staff-${hospitalName || "hospital"}-${dayjs().format("YYYYMMDD-HHmmss")}.xlsx`;

			if (disposition) {
				const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
				const name = decodeURIComponent(match?.[1] || match?.[2] || "");

				if (name) filename = name;
			}

			if (!/\.xlsx$/i.test(filename)) {
				filename = filename.replace(/\.[^/.]+$/, "") + ".xlsx";
			}

			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");

			link.href = url;
			link.download = filename;
			link.click();
			URL.revokeObjectURL(url);
		} catch (err) {
			alert("Gagal mengekspor data absensi. Coba lagi nanti.");
		} finally {
			setExporting(false);
		}
	};

	return (
		<DashboardLayout>
			<>
				<div class="text-bold text-dark mb-4">{`Absensi Staff - ${hospitalName || "Rumah Sakit"}`}</div>
				<SearchBox
					rightElement={
						<div className="d-flex align-items-end gap-2">
							<div style={{minWidth: "220px"}}>
								<label class="setting-label">Rentang Waktu</label>
								<BasicDropdown
									data={[
										{label: "1 Minggu Lalu", value: "1w"},
										{label: "1 Bulan Lalu", value: "1m"},
										{label: "1 Tahun Lalu", value: "1y"},
									]}
									placeholder="Pilih rentang"
									onSelect={val => {
										const now = dayjs();
										let start;

										if (val === "1w") start = now.subtract(1, "week");
										else if (val === "1m") start = now.subtract(1, "month");
										else start = now.subtract(1, "year");

										const startDate = start.format("YYYY-MM-DD");
										const endDate = now.format("YYYY-MM-DD");

										setDateRange({startDate, endDate});
										setDateDisplay(`${start.format("MM/DD/YYYY")} - ${now.format("MM/DD/YYYY")}`);
									}}
								/>
							</div>
							<div style={{minWidth: "220px"}}>
								<label class="setting-label">Tanggal</label>
								<DatePicker
									singleDatePicker={false}
									onChange={val => {
										const {startDate, endDate} = val || {};

										if (startDate && endDate) {
											setDateRange({startDate, endDate});
											setDateDisplay(
												`${dayjs(startDate).format("MM/DD/YYYY")} - ${dayjs(endDate).format(
													"MM/DD/YYYY",
												)}`,
											);
										}
									}}>
									<input
										class="form-control"
										placeholder="Pilih rentang tanggal"
										value={dateDisplay}
									/>
								</DatePicker>
							</div>
							<button class="btn btn-orange" disabled={exporting || !hospitalId} onClick={handleExport}>
								Export Data
							</button>
						</div>
					}
					onSearch={keyword => {
						setFilter(prev => ({...prev, search: keyword || "", page: 1}));
					}}
				/>

				{!hospitalId ? (
					<div className="p-4 text-center text-muted">Rumah sakit belum dipilih.</div>
				) : (
					<BasicTable
						columns={columns}
						currentPage={1}
						data={displayRows}
						hidePagination={true}
						limit={dataLimit}
						loading={isLoading}
						striped={true}
						title="Data Absensi Staff"
						totalItems={data?.data?.totalItems ?? rows.length}
						totalPage={1}
						onLimitChange={limit => {
							setDataLimit(limit);
							setFilter(prev => ({...prev, limit}));
						}}
						onPageChange={() => {}}
					/>
				)}
			</>
		</DashboardLayout>
	);
}

export default StaffAttendanceAdminHospitalPage;
